import React, { Suspense, lazy } from "react";
import { motion } from "framer-motion";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useAvatar } from "@/hooks/useAvatar";
import { useMirrorSurface } from "@/hooks/useMirrorSurface";
import VyvEnergy from "@/components/vyv/VyvEnergy";
import {
  MINI_MIRROR_IS_AVATURN_MODEL,
  MINI_MIRROR_SCALE,
  MINI_MIRROR_LOOK_AT_Y,
  MINI_MIRROR_CAMERA_DISTANCE,
} from "@/components/avatar/miniMirrorCalibration";

const AvatarCard = lazy(() => import("@/components/AvatarCard"));

interface AvatarTeacherProps {
  compact?: boolean;
}

const AvatarTeacher: React.FC<AvatarTeacherProps> = ({ compact = false }) => {
  const { t } = useLanguage();
  const { avatarUrl, loading: avatarLoading, hasAvatar } = useAvatar();
  // Escolha avatar × energia (user_profiles.mirror_surface) vale em TODA
  // superfície (bug 25/08). Predicado canônico: quem escolheu avatar sem ter
  // GLB próprio também cai na energia — nunca placeholder quebrado.
  const { surface, loading: surfaceLoading } = useMirrorSurface(hasAvatar);
  const showAvatar = surface === "avatar" && hasAvatar;
  const avatarHeight = compact ? "140px" : "200px";

  return (
    <motion.div
      className="relative w-full rounded-2xl overflow-hidden flex flex-col"
      style={{
        background: "linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.15) 50%, rgba(0,0,0,0.35) 100%), url('https://lluvykhqgdnbfobjixbj.supabase.co/storage/v1/object/public/pictures/IMG_4190.jpeg') center bottom/cover no-repeat",
        border: "1px solid hsl(187 72% 50% / 0.30)",
        boxShadow: "0 0 30px hsl(187 72% 50% / 0.12), inset 0 1px 0 hsl(187 72% 50% / 0.10)",
        minHeight: compact ? "160px" : "240px",
      }}
    >
      {/* Top accent line */}
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/25 to-transparent" />

      {/* Label */}
      <div className="relative pt-3 pb-1 px-6 text-center" style={{ zIndex: 1 }}>
        <p className="text-[10px] font-display tracking-[0.18em] uppercase" style={{ color: "hsl(187 72% 50% / 0.5)" }}>
          {t("language.avatarTeacher.label")}
        </p>
      </div>

      {/* Cyan halo glow behind avatar */}
      <motion.div
        className="absolute rounded-full pointer-events-none"
        style={{
          top: compact ? "10%" : "5%",
          left: "50%",
          marginLeft: compact ? -80 : -110,
          width: compact ? 160 : 220,
          height: compact ? 160 : 220,
          background: "radial-gradient(circle, rgba(0,229,255,0.08) 0%, rgba(0,229,255,0.02) 50%, transparent 70%)",
          filter: "blur(40px)",
          willChange: "transform, opacity",
        }}
        animate={{ opacity: [0.6, 1, 0.6], scale: [0.95, 1.05, 0.95] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* 3D Avatar */}
      <div style={{ marginTop: "auto" }}>
        <div className="relative" style={{ zIndex: 1 }}>
          <Suspense fallback={
            <div className="flex items-center justify-center" style={{ height: avatarHeight }}>
              <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            </div>
          }>
            {!avatarLoading && !surfaceLoading && (
              showAvatar ? (
                <AvatarCard
                  modelUrl={avatarUrl || '/avatar/model_small.glb'}
                  height={avatarHeight}
                  isAvaturnModel={MINI_MIRROR_IS_AVATURN_MODEL}
                  scaleOverride={MINI_MIRROR_SCALE}
                  cameraDistanceOverride={MINI_MIRROR_CAMERA_DISTANCE}
                  lookAtYOverride={MINI_MIRROR_LOOK_AT_Y}
                />
              ) : (
                <div style={{ height: avatarHeight }}>
                  <VyvEnergy state="idle" ratio={0.5} interactive={false} />
                </div>
              )
            )}
          </Suspense>
        </div>
      </div>

      {/* Bottom glow */}
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[200px] h-[60px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(ellipse, hsl(187 72% 50% / 0.05) 0%, transparent 70%)" }}
      />
    </motion.div>
  );
};

export default AvatarTeacher;
